import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';
import { adminApi, adminKeys, type AdminOnboardingItem } from '@/features/admin/api/admin.api';
import { ADMIN_ROUTES } from '@/features/admin/constants/routes';
import { AdminPageHeader } from '@/features/admin/components/AdminPageHeader';
import {
  isOnboardingStage,
  ONBOARDING_TABS,
} from '@/features/admin/constants/onboardingStages';
import {
  GuideOnboardingTable,
  type RowActionKey,
} from '@/features/admin/components/GuideOnboardingTable';
import { useSpringVerifyExport } from '@/features/admin/hooks/useSpringVerifyExport';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { extractApiError } from '@/shared/lib/extractApiError';
import { OnboardingStage } from '@/types';

/**
 * The guide onboarding pipeline — one page for what used to be two separate
 * admin queues.
 *
 * Every applicant appears in exactly one tab, decided server-side. The tab and
 * the search both live in the URL, so the old queue routes can redirect
 * straight to the right one and a filtered view is shareable.
 *
 * Background Check is the working tab: there is no SpringVerify API, so the
 * flow is export the batch as CSV, upload it to the vendor by hand, and come
 * back to flag whoever cleared.
 */

const DEFAULT_STAGE = OnboardingStage.BACKGROUND_CHECK;

export function AdminGuideOnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const stageParam = searchParams.get('stage');
  // An unknown ?stage= falls back to the first tab rather than rendering an
  // empty table against a stage the server does not know.
  const stage = isOnboardingStage(stageParam) ? stageParam : DEFAULT_STAGE;

  const [searchInput, setSearchInput] = useState(() => searchParams.get('q') ?? '');
  const search = useDebouncedValue(searchInput.trim(), 350);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [error, setError] = useState('');

  const exporter = useSpringVerifyExport(stage, search);

  // `replace`, or every debounced keystroke becomes a history entry and the
  // browser's Back button stops being usable.
  useEffect(() => {
    const next = new URLSearchParams();
    if (stage !== DEFAULT_STAGE) next.set('stage', stage);
    if (search) next.set('q', search);
    setSearchParams(next, { replace: true });
  }, [stage, search, setSearchParams]);

  // An effect rather than an inline handler: both inputs can also change via
  // the Back button or a shared link.
  useEffect(() => {
    setPage(0);
  }, [search, stage]);

  const params = {
    page: page + 1,
    limit: rowsPerPage,
    stage,
    // Spread rather than `search: search || undefined` — react-query hashes an
    // explicit `undefined` differently from an absent key, which would split
    // the cache against every other caller.
    ...(search ? { search } : {}),
  };

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: adminKeys.onboarding(params),
    queryFn: () => adminApi.getOnboardingPipeline(params),
    // Keeps the table from blanking between pages and debounced searches.
    placeholderData: keepPreviousData,
  });

  const actionMutation = useMutation({
    mutationFn: ({ row, action }: { row: AdminOnboardingItem; action: RowActionKey }) => {
      // The notes are mandatory server-side and the admin is already stamped
      // into the audit trail, so a one-click decision generates its own.
      const on = dayjs().format('D MMM YYYY');
      const by =
        [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'an admin';

      switch (action) {
        case 'pass':
          return adminApi.passVerification(
            row.id,
            `Marked verified by SpringVerify by ${by} on ${on}`.slice(0, 2000),
            // Preserve the vendor's candidate id, which is what reconciles a
            // returned verdict against the CSV's Profile ID column.
            row.verificationReference
          );
        case 'retry':
          // FAILED -> PENDING puts them back at the start of the check. There
          // is no FAILED -> IN_PROGRESS edge, so this is its own endpoint.
          return adminApi.reopenVerification(
            row.id,
            `Re-opened for retry by ${by} on ${on}`.slice(0, 2000)
          );
        default:
          return Promise.reject(new Error(`Unsupported row action: ${action}`));
      }
    },
    onSuccess: () => {
      // Coarse on purpose: a decision moves the row between tabs and changes
      // several counts, so there is nothing meaningful left to keep cached.
      queryClient.invalidateQueries({ queryKey: adminKeys.all });
      setError('');
    },
    onError: (err: unknown) => {
      const { status, message } = extractApiError(err, 'Action failed. Please try again.');
      // The lifecycle service compare-and-sets, so a conflict means someone
      // else already moved this guide. Retrying would fail the same way.
      setError(
        status === 409
          ? 'Another admin already actioned this applicant. The list has been refreshed.'
          : message
      );
      if (status === 409) queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });

  const selectStage = (next: OnboardingStage) => {
    setPage(0);
    // Pushed, not replaced: switching tabs is a navigation worth going back
    // from, unlike the per-keystroke writes the effect above makes.
    const nextParams = new URLSearchParams();
    if (next !== DEFAULT_STAGE) nextParams.set('stage', next);
    if (search) nextParams.set('q', search);
    setSearchParams(nextParams);
  };

  const openDetail = (row: AdminOnboardingItem) => {
    // Carry the tab along so the detail page's back link returns here rather
    // than to a hardcoded queue.
    navigate(ADMIN_ROUTES.review(row.id), {
      state: { from: ADMIN_ROUTES.onboarding(stage) },
    });
  };

  const handleAction = (row: AdminOnboardingItem, action: RowActionKey) => {
    // A profile decision is made against the whole profile, not from a row.
    if (action === 'review') {
      openDetail(row);
      return;
    }
    setError('');
    actionMutation.mutate({ row, action });
  };

  const activeTab = ONBOARDING_TABS.findIndex((tab) => tab.stage === stage);
  const description = ONBOARDING_TABS[activeTab]?.description;
  const total = data?.pagination.total ?? 0;
  const isBackgroundCheck = stage === OnboardingStage.BACKGROUND_CHECK;

  const exportLabel = exporter.progress
    ? exporter.progress.phase === 'fetching'
      ? 'Collecting…'
      : `Marking ${exporter.progress.done} of ${exporter.progress.total}…`
    : 'Export to Excel';

  return (
    <Box>
      <AdminPageHeader
        title="Guide Onboarding"
        subtitle="Background check, profile review and go-live in one pipeline. Each applicant sits in exactly one tab."
        actions={
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Name, email or phone"
              inputProps={{ 'aria-label': 'Search applicants' }}
              sx={{ minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton size="small" aria-label="Clear search" onClick={() => setSearchInput('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            {isBackgroundCheck && (
              <Button
                variant="contained"
                startIcon={
                  exporter.progress ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                disabled={exporter.isBusy}
                aria-busy={exporter.isBusy}
                onClick={exporter.start}
              >
                {exportLabel}
              </Button>
            )}
          </Box>
        }
      />

      {/* Debounced search is otherwise entirely silent to a screen reader. */}
      <Box role="status" aria-live="polite" sx={visuallyHidden}>
        {isLoading
          ? 'Loading applicants'
          : search
            ? `${total} applicants matching ${search}`
            : `${total} applicants`}
      </Box>

      {error && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {exporter.outcome && (
        <Alert
          severity={exporter.outcome.severity}
          role={exporter.outcome.severity === 'error' ? 'alert' : 'status'}
          sx={{ mb: 2 }}
          onClose={exporter.dismissOutcome}
          action={
            exporter.outcome.retryableIds?.length ? (
              <Button
                size="small"
                color="inherit"
                onClick={() => exporter.retryMarking(exporter.outcome!.retryableIds!)}
              >
                Retry marking
              </Button>
            ) : undefined
          }
        >
          {exporter.outcome.message}
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Tabs
          value={activeTab === -1 ? 0 : activeTab}
          onChange={(_, value: number) => selectStage(ONBOARDING_TABS[value].stage)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 1 }}
        >
          {ONBOARDING_TABS.map((tab) => (
            <Tab key={tab.stage} label={tab.label} />
          ))}
        </Tabs>

        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 3, pt: 2, pb: 0.5 }}>
            {description}
          </Typography>
        )}

        <GuideOnboardingTable
          stage={stage}
          rows={data?.data ?? []}
          isLoading={isLoading}
          isFetching={isFetching}
          isError={isError}
          onRetry={() => void refetch()}
          search={search}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(0);
          }}
          onOpen={openDetail}
          onAction={handleAction}
          pendingRowId={actionMutation.isPending ? actionMutation.variables?.row.id : undefined}
          isActionPending={actionMutation.isPending}
        />
      </Paper>

      {/* Marking an applicant as sent cannot be undone — the lifecycle exposes
          no way back from in_progress to pending — so every batch confirms. */}
      <Dialog open={Boolean(exporter.pendingConfirm)} onClose={exporter.cancel}>
        <DialogTitle>Export to SpringVerify?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This downloads {exporter.pendingConfirm?.length ?? 0} applicant(s)
            {search ? ` matching "${search}"` : ''} and marks them as sent to
            SpringVerify, so they will not appear in your next export. This cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={exporter.cancel}>Cancel</Button>
          <Button variant="contained" onClick={exporter.confirm}>
            Download and mark as sent
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
